import React, { useEffect, useMemo, useState } from "react";
import {
	BookingContentName,
	BookingContentWrap,
	ButtonUserWrap,
	MyBookingWrapper,
	AdminTransactionAddButtton,
} from "../MyBooking/styled/DesStyled";
import { CircularProgress, FormControlLabel, Radio, RadioGroup, Switch, TextField } from "@material-ui/core";
import StringManipulator from "../../../utils/StringManipulator";
import {
	getPaymentSurchargeAdmin,
	savePaymentSurchargeAdmin,
	type PaymentSurchargeConfig,
} from "../../../services/PaymentSurchargeAdminService";
import PermissionGate from "../../controls/auth/PermissionGate";

const PREVIEW_BASE = 50;

function round2(n: number): number {
	return Math.round(n * 100) / 100;
}

export default function PaymentSurchargeSettings() {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState<PaymentSurchargeConfig>({
		enabled: false,
		type: "percent",
		value: 0,
		currency: "USD",
	});

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				setLoading(true);
				const data = await getPaymentSurchargeAdmin();
				if (!mounted) return;
				setForm({
					enabled: Boolean(data.enabled),
					type: data.type === "fixed" ? "fixed" : data.type === "percent" ? "percent" : "percent",
					value: data.value ?? 0,
					currency: data.currency || "USD",
				});
			} catch (err: any) {
				alert(`Không tải được cấu hình: ${err?.response?.data?.message || err?.message || "Thử lại"}`);
			} finally {
				setLoading(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, []);

	const previewFee = useMemo(() => {
		if (!form.enabled) return 0;
		if (form.type === "percent") {
			return round2(PREVIEW_BASE * (Number(form.value) / 100));
		}
		return round2(Number(form.value));
	}, [form.enabled, form.type, form.value]);

	const previewTotal = useMemo(() => round2(PREVIEW_BASE + previewFee), [previewFee]);

	const onSave = async () => {
		try {
			setSaving(true);
			if (!form.enabled) {
				await savePaymentSurchargeAdmin({ enabled: false, currency: form.currency });
			} else {
				await savePaymentSurchargeAdmin({
					enabled: true,
					type: form.type === "fixed" ? "fixed" : "percent",
					value: Number(form.value),
					currency: form.currency,
				});
			}
			alert("Saved PayPal surcharge settings.");
		} catch (err: any) {
			alert(`Save failed: ${err?.response?.data?.message || err?.message || "Try again"}`);
		} finally {
			setSaving(false);
		}
	};

	return (
		<MyBookingWrapper>
			<BookingContentWrap>
				<BookingContentName>PayPal surcharge (non-Stripe checkout)</BookingContentName>

				<div style={{ marginBottom: 16, color: "#6B7280", fontSize: 13, maxWidth: 640 }}>
					Add a processing fee on orders paid via PayPal (wallet, guest card, Google Pay via PayPal). Stripe
					and OnePay are not affected. Amount is calculated on the server.
				</div>

				{loading ? (
					<div style={{ textAlign: "center", padding: 32 }}>
						<CircularProgress />
					</div>
				) : (
					<div style={{ display: "grid", gap: 20, maxWidth: 560 }}>
						<FormControlLabel
							control={
								<Switch
									checked={form.enabled}
									onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
									color="primary"
								/>
							}
							label="Enable surcharge for PayPal flows"
						/>

						<div
							style={{ opacity: form.enabled ? 1 : 0.45, pointerEvents: form.enabled ? "auto" : "none" }}>
							<div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>Fee type</div>
							<RadioGroup
								row
								value={form.type === "fixed" ? "fixed" : "percent"}
								onChange={(e) =>
									setForm((p) => ({
										...p,
										type: e.target.value === "fixed" ? "fixed" : "percent",
									}))
								}>
								<FormControlLabel
									value="percent"
									control={<Radio color="primary" />}
									label="Percent (%)"
								/>
								<FormControlLabel
									value="fixed"
									control={<Radio color="primary" />}
									label="Fixed amount (USD)"
								/>
							</RadioGroup>

							<TextField
								type="number"
								label={form.type === "fixed" ? "Amount (USD)" : "Percent"}
								value={form.value}
								onChange={(e) =>
									setForm((p) => ({
										...p,
										value: parseFloat(e.target.value) || 0,
									}))
								}
								variant="outlined"
								size="small"
								fullWidth
								style={{ marginTop: 12 }}
								inputProps={{
									min: 0,
									max: form.type === "percent" ? 100 : undefined,
									step: form.type === "percent" ? 0.1 : 0.01,
								}}
								disabled={saving}
							/>
						</div>

						<div
							style={{
								background: "#F9FAFB",
								border: "1px solid #E5E7EB",
								borderRadius: 8,
								padding: "12px 16px",
								fontSize: 13,
								lineHeight: 1.7,
							}}>
							<b>Preview</b> (example order {StringManipulator.formatCurrency(PREVIEW_BASE, "USD")} USD):
							<br />
							Surcharge:{" "}
							<b>
								{StringManipulator.formatCurrency(previewFee, "USD")} USD
								{form.enabled && form.type === "percent" ? ` (${Number(form.value)}%)` : ""}
							</b>
							<br />
							Customer total: <b>{StringManipulator.formatCurrency(previewTotal, "USD")} USD</b>
						</div>
					</div>
				)}

				<ButtonUserWrap style={{ marginTop: 24 }}>
					<PermissionGate permission="settings.manage">
						<AdminTransactionAddButtton
							onClick={onSave}
							disabled={loading || saving}>
							{saving ? "Saving..." : "Save"}
						</AdminTransactionAddButtton>
					</PermissionGate>
				</ButtonUserWrap>
			</BookingContentWrap>
		</MyBookingWrapper>
	);
}
